import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useAnalytics } from './hooks/useAnalytics';

// Components
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import Layout from './components/Layout';
import { Toaster } from './components/Toaster';
import { StartupSubNav } from './components/StartupSubNav';

// Pages
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import StartupsPage from './pages/StartupsPage';
import StartupDetailPage from './pages/StartupDetailPage';
import NewStartupPage from './pages/NewStartupPage';
import FinancialsPage from './pages/FinancialsPage';
import BetaPage from './pages/BetaPage';
import FeedbackPage from './pages/FeedbackPage';
import FeedbackFormPage from './pages/FeedbackFormPage';
import DashboardPage from './pages/DashboardPage';
import CoursePage from './pages/CoursePage';
import PipelinePage from './pages/PipelinePage';
import PipelineListPage from './pages/PipelineListPage';
import WelcomePage from './pages/WelcomePage';
import CreditsPage from './pages/CreditsPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import PublicResultsPage from './pages/PublicResultsPage';
import PublicReportPage from './pages/PublicReportPage';
import ContactPage from './pages/ContactPage';
import AdminPage from './pages/AdminPage';

/** Route guard: redirects to /auth with return URL if not authenticated */
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) return <>{children}</>;
  // FIX: #4a — save intended URL before redirecting to auth
  sessionStorage.setItem('loginRedirect', location.pathname + location.search);
  return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
}

function AnalyticsTracker() {
  useAnalytics();
  return null;
}

export default function App() {
  const { loadUser } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  return (
    <ErrorBoundary>
      <OfflineBanner />
      <Toaster />
      <BrowserRouter>
        <AnalyticsTracker />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/feedback/:token" element={<FeedbackFormPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/results/:id" element={<PublicResultsPage />} />
          <Route path="/report/:startupId" element={<PublicReportPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Protected routes — wrapped in Layout */}
          <Route
            path="/startups"
            element={
              <PrivateRoute>
                <Layout>
                  <StartupsPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/startups/new"
            element={
              <PrivateRoute>
                <Layout>
                  <NewStartupPage />
                </Layout>
              </PrivateRoute>
            }
          />
          {/* UX 4: All startup sub-pages wrapped in StartupSubNav for consistent sidebar */}
          <Route
            path="/startups/:id"
            element={
              <PrivateRoute>
                <Layout>
                  <StartupSubNav><StartupDetailPage /></StartupSubNav>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/startups/:id/financials"
            element={
              <PrivateRoute>
                <Layout>
                  <StartupSubNav><FinancialsPage /></StartupSubNav>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/startups/:id/beta"
            element={
              <PrivateRoute>
                <Layout>
                  <StartupSubNav><BetaPage /></StartupSubNav>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/startups/:id/feedback"
            element={
              <PrivateRoute>
                <Layout>
                  <StartupSubNav><FeedbackPage /></StartupSubNav>
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/startups/:id/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/course"
            element={
              <PrivateRoute>
                <Layout>
                  <CoursePage />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Credits */}
          <Route
            path="/credits"
            element={
              <PrivateRoute>
                <Layout>
                  <CreditsPage />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Welcome page — shown after signup */}
          <Route
            path="/welcome"
            element={
              <PrivateRoute>
                <WelcomePage />
              </PrivateRoute>
            }
          />

          {/* Pipeline — list uses Layout; detail is full-screen with its own nav */}
          <Route
            path="/pipeline"
            element={
              <PrivateRoute>
                <Layout>
                  <PipelineListPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/pipeline/:id"
            element={
              <PrivateRoute>
                <PipelinePage />
              </PrivateRoute>
            }
          />

          {/* Admin — no Layout wrapper, handles its own UI */}
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <AdminPage />
              </PrivateRoute>
            }
          />

          {/* Bug 4: Common deep-link aliases — redirect to correct paths */}
          <Route path="/startups/:id/beta-users" element={
            <PrivateRoute><Layout><StartupSubNav><BetaPage /></StartupSubNav></Layout></PrivateRoute>
          } />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
